import { Extension, getMarkRange, getMarkType } from "@tiptap/core";

export enum LinkMenuState {
  HIDDEN = "HIDDEN",
  VIEW = "VIEW",
  EDIT = "EDIT",
}

export interface LinkBubbleMenuHandlerStorage {
  state: LinkMenuState;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    linkBubbleMenu: {
      openLinkBubbleMenu: () => ReturnType;
      editLinkInBubbleMenu: () => ReturnType;
      closeLinkBubbleMenu: () => ReturnType;
    };
  }
}

/**
 * Extension that manages link bubble menu state.
 *
 * The editor selection is the source of truth for which link is being edited.
 * When opening on an existing link, extendMarkRange selects the full link text.
 * The VIEW/EDIT state machine lives in editor.storage so the React component
 * can read it reactively via useEditorState.
 */
export const LinkBubbleMenuHandler = Extension.create<
  Record<string, never>,
  LinkBubbleMenuHandlerStorage
>({
  name: "linkBubbleMenu",

  addStorage() {
    return { state: LinkMenuState.HIDDEN };
  },

  addCommands() {
    return {
      openLinkBubbleMenu:
        () =>
        ({ editor, chain, dispatch }) => {
          if (editor.isActive("link")) {
            // Select the entire link so the bubble menu positions on it
            chain().extendMarkRange("link").focus().run();
          } else if (editor.state.selection.empty) {
            // Expand to the word at cursor so the bubble menu can anchor
            const { $from } = editor.state.selection;
            const text = $from.parent.textContent;
            const offset = $from.parentOffset;
            let start = offset;
            let end = offset;
            while (start > 0 && /\w/.test(text[start - 1])) start--;
            while (end < text.length && /\w/.test(text[end])) end++;
            if (start < end) {
              const base = $from.start();
              chain()
                .setTextSelection({ from: base + start, to: base + end })
                .focus()
                .run();
            } else {
              // Cursor not on a word — nothing to link
              return false;
            }
          }
          if (dispatch) {
            this.storage.state = LinkMenuState.EDIT;
          }
          return true;
        },

      editLinkInBubbleMenu:
        () =>
        ({ dispatch }) => {
          if (dispatch) {
            this.storage.state = LinkMenuState.EDIT;
          }
          return true;
        },

      closeLinkBubbleMenu:
        () =>
        ({ dispatch }) => {
          if (dispatch) {
            this.storage.state = LinkMenuState.HIDDEN;
          }
          return true;
        },
    };
  },

  onTransaction() {
    const { editor } = this;
    const isOnLink =
      editor.isActive("link") ||
      !!getMarkRange(
        editor.state.selection.$from,
        getMarkType("link", editor.schema),
      );

    if (isOnLink) {
      // Auto-open VIEW when cursor lands on a link.
      // Don't override EDIT (user is actively editing).
      if (this.storage.state === LinkMenuState.HIDDEN) {
        this.storage.state = LinkMenuState.VIEW;
      }
    } else if (this.storage.state === LinkMenuState.VIEW) {
      // Auto-close VIEW when cursor leaves a link
      this.storage.state = LinkMenuState.HIDDEN;
    } else if (
      this.storage.state === LinkMenuState.EDIT &&
      editor.state.selection.empty
    ) {
      // Close EDIT when selection collapses (user clicked away).
      // Keep EDIT alive while there's a non-empty selection so the user
      // can create a new link on selected text.
      this.storage.state = LinkMenuState.HIDDEN;
    }
  },
});
